import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReplayRounded, ZoomOutMap } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { Alert, Box, Divider, Grid, IconButton, Stack, Theme, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ArrowLeftIcon, ArrowRightIcon } from '@mui/x-date-pickers';
import { useSearchParams } from 'react-router-dom';
import { Map as LeafletMap } from 'leaflet';
import moment from 'moment-timezone';
import { useSafetyPredictionServiceGetBerths } from '@/api/ui/queries';
import { BerthSafetyResponse, SafetyPrediction, SafetyPredictionTrigger } from '@/api/ui/requests';
import ActiveIcon from '@/assets/svg/marker-checked.svg?react';
import SimulationView from '@/assets/svg/simulation.svg?react';
import AutoComplete from '@/components/common/AutoComplete/AutoComplete';
import ApiErrorAlert from '@/components/feedback/Error/ApiErrorAlert';
import LoadingIndicator from '@/components/feedback/LoadingIndicator';
import NoScrollBox from '@/components/layout/NoScrollBox/NoScrollBox';
import { ControlProps, MapMarkerProps } from '@/components/maps/Map/types';
import useToBerthSafetyNavigate from '@/features/SafetyPrediction/BerthDashboard/useToBerthSafetyNavigate';
import { MarkerIcon } from '@/features/SafetyPrediction/Icons';
import { OVERVIEW_MAP_PREDICTION_DAYS_IN_ADVANCE } from '@/features/SafetyPrediction/Overview/types';
import { useGraphConfig } from '@/hooks/useGraphConfig';
import useIsMscOrganisation from '@/hooks/useIsMscOrganisation';
import useMapParams from '@/hooks/useMapParams';
import useOrganisation from '@/hooks/useOrganisation';
import usePageTitle from '@/hooks/usePageTitle';
import useSearchParamState from '@/hooks/useSearchParamState';
import { Analytics } from '@/services/analytics';
import { SafetyPredictionTriggerStaticValues } from '@/types';

const SimulationMap = lazy(() => import('@/components/maps/Map/BerthMap'));
const WARNINGS_MAX_HEIGHT = 230;
const WARNINGS_POPUP_WIDTH = 420;
const EMPTY_POPUP_WIDTH = 240;
const DISABLED_POPUP_WIDTH = 300;
/** MSC reads mooring-line percentages against MBL, mirroring LineForceChart's `tooltipLabel: 'MBL'`. */
const MSC_LINE_FORCE_UNIT = 'MBL';

function BerthIcon({ enabled, color, theme, useActiveIcon }: { enabled: boolean; color: string; theme?: Theme; useActiveIcon: boolean }) {
  let fill = theme?.palette?.disabled?.main;
  if (enabled) {
    fill = color ?? theme?.palette?.below_lower_limit?.main;
  }

  return useActiveIcon ? <ActiveIcon fill={fill} /> : <MarkerIcon fill={fill} />;
}

function warningsBySeverity(warnings: SafetyPrediction[]): SafetyPrediction[] {
  return [...warnings].sort((a, b) => b.percentage - a.percentage);
}

type VesselClassWarnings = {
  dmaCaseId: string;
  header: string;
  warnings: SafetyPrediction[];
  worstPercentage: number;
};

function vesselClassHeader(warning: SafetyPrediction): string {
  const vesselClassName = warning.customerVesselDefinition?.customerVesselClass ?? warning.vesselClass;
  const classifier = dmaCaseClassifier(warning.dmaCaseId);
  return classifier ? `${vesselClassName} - ${classifier}` : vesselClassName;
}

function groupWarningsByVesselClass(warnings: SafetyPrediction[]): VesselClassWarnings[] {
  const groups = new Map<string, SafetyPrediction[]>();

  for (const warning of warnings) {
    const group = groups.get(warning.dmaCaseId);
    if (group) group.push(warning);
    else groups.set(warning.dmaCaseId, [warning]);
  }

  return [...groups.entries()]
    .map(([dmaCaseId, groupWarnings]) => {
      const sorted = [...groupWarnings].sort((a, b) => b.percentage - a.percentage);
      return {
        dmaCaseId,
        header: vesselClassHeader(sorted[0]),
        warnings: sorted,
        worstPercentage: sorted[0]?.percentage ?? 0,
      };
    })
    .sort((a, b) => b.worstPercentage - a.worstPercentage);
}

function dmaCaseClassifier(dmaCaseId: string): string | undefined {
  const classifierParts = dmaCaseId.split(/-(?:SPECIFIC|GENERIC)-/);
  if (classifierParts.length < 2) {
    return undefined;
  }

  const classifier = classifierParts[classifierParts.length - 1].replace(/_/g, ' ').trim();
  return classifier && classifier !== 'default' ? classifier : undefined;
}

function getWarningPopupWidth(berth: BerthSafetyResponse, warnings: SafetyPrediction[]): number {
  if (warnings.length > 0) return WARNINGS_POPUP_WIDTH;
  return berth.enabled ? EMPTY_POPUP_WIDTH : DISABLED_POPUP_WIDTH;
}

function WarningPopUp({ berth, warnings, popupWidth }: { berth: BerthSafetyResponse; warnings: SafetyPrediction[]; popupWidth: number }) {
  const { berthId, name, enabled, timezones, portName } = berth;
  const theme = useTheme();
  const worstWarning = warnings.at(0);
  const vesselClasses = useMemo(() => groupWarningsByVesselClass(warnings), [warnings]);
  const title = portName ? `${portName} \u2013 ${name}` : name;

  return (
    <Box sx={{ width: popupWidth, padding: 0 }}>
      <Grid container sx={{ width: '100%' }}>
        <Grid item xs={12}>
          <Grid direction="row" container alignItems="center">
            <Grid item sx={{ paddingRight: 1 }}>
              <BerthIcon color={worstWarning?.threshold?.color} enabled={berth.enabled} theme={theme} useActiveIcon={false} />
            </Grid>
            <Grid item xs>
              <Typography variant="bodySemiBoldMedium" sx={{ color: '#2a3448' }}>
                {title}
              </Typography>
            </Grid>
          </Grid>
          {!enabled && (
            <Grid item paddingTop={1}>
              <Typography variant="header">
                No safety predictions available! <br />
                Berth does not actively receive metocean data.
              </Typography>
            </Grid>
          )}
          {enabled &&
            (vesselClasses.length > 0 ? (
              <Box
                sx={
                  warnings.length > 2
                    ? {
                        maxHeight: WARNINGS_MAX_HEIGHT,
                        overflowY: 'auto',
                        pr: 1,
                        pb: 1.5,
                      }
                    : undefined
                }
              >
                {vesselClasses.map((vesselClass, index) => (
                  <Box key={`${berthId}-${vesselClass.dmaCaseId}`}>
                    {index > 0 && <Divider sx={{ my: 1 }} />}
                    <VesselClassWarningDetail vesselClass={vesselClass} berthId={berthId} berthTimeZone={timezones?.at(0)} />
                  </Box>
                ))}
              </Box>
            ) : (
              <Grid item paddingTop={1}>
                <Typography variant="header">No warnings found</Typography>
              </Grid>
            ))}
        </Grid>
      </Grid>
    </Box>
  );
}

function VesselClassWarningDetail({
  berthId,
  berthTimeZone,
  vesselClass,
}: {
  berthId: string;
  berthTimeZone: string;
  vesselClass: VesselClassWarnings;
}) {
  const { getDefaultGraphConfig } = useGraphConfig();
  const isMscOrganisation = useIsMscOrganisation();

  return (
    <Grid item marginLeft={5} marginRight={2}>
      <Typography variant="bodySemiBoldSmall" sx={{ display: 'block', wordBreak: 'break-word' }}>
        {vesselClass.header}
      </Typography>
      {vesselClass.warnings.map((warning) => {
        const timestamp = moment(warning.forecast.timestamp).tz(berthTimeZone);
        const today = moment().tz(berthTimeZone).isSame(timestamp, 'day');
        const timeIndication = (today ? 'at today' : timestamp.fromNow()) + ` ${timestamp.format('DD MMM HH:mm z')}`;
        const unitLabel =
          isMscOrganisation && warning.trigger === SafetyPredictionTrigger.LINE_FORCE
            ? MSC_LINE_FORCE_UNIT
            : warning.threshold?.abbr ?? getDefaultGraphConfig(warning.trigger).defaultLabel;

        return (
          <Grid key={`${berthId}-${warning.dmaCaseId}-${warning.trigger}`} item paddingTop={1}>
            <Typography variant="bodySemiBoldSmall" sx={{ display: 'block', color: warning.threshold?.color }}>
              Exceeding{' '}
              {[SafetyPredictionTriggerStaticValues[warning.trigger].warningSubject, warning.threshold?.label].filter(Boolean).join(' ')}
            </Typography>
            <Typography variant="bodySemiBoldSmall" sx={{ display: 'block', color: warning.threshold?.color }}>
              {warning.percentage.toFixed(0)}% {unitLabel} {timeIndication}
            </Typography>
          </Grid>
        );
      })}
    </Grid>
  );
}

function SafetyPredictionSimulationView() {
  usePageTitle('Simulation - Map', <SimulationView width={32} key={'Simulation - Map'} />);
  const { selectedOrganisationId } = useOrganisation();
  const { defaultPeriod } = useGraphConfig();
  const { navigateToBerthSafety } = useToBerthSafetyNavigate();
  const [startDate, setStartDate] = useSearchParamState('date', moment().subtract(1, 'hours').startOf('hour').toISOString(true));
  const [berthsOptions, setBerthsOptions] = useState<BerthSafetyResponse[]>([]);
  const [berth, setBerth] = useState<BerthSafetyResponse | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const popupCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (popupCloseTimer.current) {
        clearTimeout(popupCloseTimer.current);
      }
    },
    []
  );

  const {
    isError,
    error,
    isLoading,
    data: berths,
  } = useSafetyPredictionServiceGetBerths(
    {
      xSelectedOrganisationId: selectedOrganisationId,
      startTime: startDate,
      numberOfDays: OVERVIEW_MAP_PREDICTION_DAYS_IN_ADVANCE,
    },
    [selectedOrganisationId, startDate],
    {
      refetchOnWindowFocus: false,
      refetchInterval: 180000, // 3 minute
    }
  );

  const { zoomLevel, center, setMapParams } = useMapParams();
  const periodLabel = useMemo(
    () =>
      `${moment(startDate).format('DD MMM HH:mm')} - ${moment(startDate)
        .add(OVERVIEW_MAP_PREDICTION_DAYS_IN_ADVANCE, 'days')
        .format('DD MMM HH:mm')}`,
    [startDate]
  );

  useEffect(() => {
    Analytics.track('Simulation', {});
  }, []);

  useEffect(() => {
    if (berths) {
      setBerthsOptions(
        berths
          .filter((berth) => berth.enabled)
          .sort((berth1, berth2) => (berth1.portName ?? berth1.name).localeCompare(berth2.portName ?? berth2.name))
          .filter(
            (berth, index, array) => array.findIndex((elem) => (elem.portName ?? elem.name) === (berth.portName ?? berth.name)) === index
          )
      );
    }
  }, [berths]);
  useEffect(() => {
    if (berth) {
      searchParams.set('lat', berth.location.latitude.toString());
      searchParams.set('lng', berth.location.longitude.toString());
      searchParams.set('zoom', '14');
      searchParams.set('berthId', berth.berthId);
      setSearchParams(searchParams);
    }
  }, [berth, searchParams, setSearchParams]);

  const navigateToBerth = useCallback(
    (berth: BerthSafetyResponse, maxWarning?: SafetyPrediction) => {
      if (!berth) return;

      const { timezones, berthId } = berth;

      const requestTimestamp = timezones?.at(0) ? moment().tz(timezones.at(0)) : moment();

      navigateToBerthSafety({
        berthId: berthId,
        loadingCondition: maxWarning?.loadingCondition,
        trigger: maxWarning?.trigger,
        dmaCaseId: maxWarning?.dmaCaseId,
        isOperationalVessel: false,
        vesselDisposition: maxWarning?.vesselDisposition,
        requestTimestamp,
        days: defaultPeriod,
      });
    },
    [navigateToBerthSafety, defaultPeriod]
  );

  const resetZoom = useCallback(() => {
    searchParams.delete('lat');
    searchParams.delete('lng');
    searchParams.delete('zoom');
    searchParams.delete('berthId');
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const isActive = useCallback(
    (berthId: string) => {
      return berth ? berth.berthId === berthId : searchParams.get('berthId') === berthId;
    },
    [berth, searchParams]
  );

  const markerIconAsString = useCallback(
    (enabled: boolean, color: string, useActiveIcon: boolean) => {
      return <BerthIcon enabled={enabled} color={color} useActiveIcon={useActiveIcon} theme={theme} />;
    },
    [theme]
  );

  const markers: MapMarkerProps[] = useMemo(() => {
    if (!berths) return [];

    return berths.map((berth) => {
      const vesselWarnings = warningsBySeverity(berth.warnings ?? []);
      const maxWarning = vesselWarnings[0];
      const popupWidth = getWarningPopupWidth(berth, vesselWarnings);

      return {
        latitude: berth.location.latitude,
        longitude: berth.location.longitude,
        markerIcon: {
          icon: markerIconAsString(berth.enabled, maxWarning?.threshold?.color, false),
          iconSize: 24,
          value: maxWarning?.percentage,
          color: maxWarning?.threshold?.color,
          enabled: berth.enabled,
        },
        activeMarkerIcon: {
          icon: markerIconAsString(berth.enabled, maxWarning?.threshold?.color, true),
          iconSize: 30,
          value: maxWarning?.percentage,
          color: maxWarning?.threshold?.color,
          enabled: berth.enabled,
        },
        popup: {
          content: <WarningPopUp berth={berth} warnings={vesselWarnings} popupWidth={popupWidth} />,
          closeButton: false,
          maxWidth: popupWidth,
        },
        active: isActive(berth.berthId),
        onMouseOver: (event) => {
          if (popupCloseTimer.current) {
            clearTimeout(popupCloseTimer.current);
            popupCloseTimer.current = null;
          }

          const marker = event.target;
          marker.openPopup();

          const popupElement = marker.getPopup()?.getElement();
          if (popupElement) {
            popupElement.onmouseenter = () => {
              if (popupCloseTimer.current) {
                clearTimeout(popupCloseTimer.current);
                popupCloseTimer.current = null;
              }
            };
            popupElement.onmouseleave = () => marker.closePopup();
          }
        },
        onMouseOut: (event) => {
          const marker = event.target;
          popupCloseTimer.current = setTimeout(() => marker.closePopup(), 250);
        },
        onClick: (_, map: LeafletMap) => {
          setMapParams(map);
          navigateToBerth(berth, maxWarning);
        },
      };
    });
  }, [berths, isActive, navigateToBerth, setMapParams, markerIconAsString]);

  const controls: ControlProps[] = useMemo(
    () => [
      {
        position: 'topleft',
        children: (
          <Stack
            direction={'row'}
            sx={{
              bgcolor: 'background.default',
              width: { xs: '48vw', sm: '42vw', md: '40vw', lg: '30vw' },
              border: '2px solid #999999',
              borderRadius: 2,
              padding: 1,
            }}
          >
            <Box sx={{ width: { xs: '48vw', sm: '42vw', md: '40vw', lg: '30vw' } }}>
              <AutoComplete<BerthSafetyResponse>
                options={berthsOptions}
                value={berthsOptions?.find((b) => b.berthId == searchParams.get('berthId'))}
                setValue={setBerth}
                displayFunction={(berth: BerthSafetyResponse) => berth.portName ?? berth.name}
                startAdornment={<SearchIcon />}
                placeHolder={'Search for a port'}
                equalsFunction={(berth1: BerthSafetyResponse, berth2: BerthSafetyResponse) => berth1.berthId === berth2.berthId}
                optionsLimit={50}
              />
            </Box>
            <Box>
              <IconButton title={'Reset zoom'} onClick={() => resetZoom()}>
                <ZoomOutMap fontSize={'large'} />
              </IconButton>
            </Box>
          </Stack>
        ),
      },
      {
        position: 'topright',
        children: (
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: 'background.default',
              border: '2px solid #777777',
              borderRadius: 2,
              boxShadow: 2,
              display: 'flex',
              gap: 0.5,
              minHeight: 48,
              paddingX: 1,
              whiteSpace: 'nowrap',
            }}
          >
            <Tooltip title={'Simulation overview of the previous day'}>
              <span>
                <IconButton
                  onClick={() => setStartDate(moment(startDate).subtract(1, 'days').toISOString())}
                  disabled={moment(startDate).diff(moment(), 'days') < -14}
                  size="small"
                >
                  <ArrowLeftIcon fontSize={'medium'} />
                </IconButton>
              </span>
            </Tooltip>

            <Typography
              variant="header"
              sx={{
                minWidth: { xs: 190, md: 245 },
                textAlign: 'center',
              }}
            >
              {periodLabel}
            </Typography>

            <Tooltip title={'Simulation overview of the next day'}>
              <span>
                <IconButton
                  onClick={() => setStartDate(moment(startDate).add(1, 'days').toISOString())}
                  disabled={moment(startDate).diff(moment(), 'days') > 1}
                  size="small"
                >
                  <ArrowRightIcon fontSize={'medium'} />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={'Go back to today'}>
              <span>
                <IconButton
                  onClick={() => {
                    searchParams.delete('date');
                    setSearchParams(searchParams);
                  }}
                  disabled={moment(startDate).isSame(moment(), 'day')}
                  size="medium"
                >
                  <ReplayRounded fontSize={'large'} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [berthsOptions, periodLabel, resetZoom, searchParams, setSearchParams, setStartDate, startDate]
  );

  if (berths && berths.length > 0) {
    return (
      <NoScrollBox component="div">
        <Suspense fallback={<LoadingIndicator message={'Loading berth locations and safety predictions...'} />}>
          <SimulationMap
            mapMarkers={markers}
            controls={controls}
            showZoomControl={true}
            clustering={true}
            zoomLevel={zoomLevel}
            center={center}
          />
        </Suspense>
      </NoScrollBox>
    );
  }

  if (isError) {
    return (
      <Box sx={{ marginX: 'auto', marginY: 10, width: '50vw' }}>
        <ApiErrorAlert error={error} />
      </Box>
    );
  }

  if (!isLoading && markers.length === 0) {
    return (
      <Box sx={{ marginX: 'auto', marginY: 10, width: '50vw' }}>
        <Alert severity="warning" color={'warning'}>
          No berths found.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ marginX: 'auto', marginY: 20, width: '50vw' }}>
      <LoadingIndicator message={'Retrieving berth information for your organisation...'} />
    </Box>
  );
}

export default SafetyPredictionSimulationView;
